import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatMoney, type PublicPlan, type SignupPlans, type StartResult } from '@buildiary/core';
import { useLang, useT } from '../app/context';
import { useHost } from '../app/host';
import { LANG_NAME, LANGS } from '../i18n';
import { ApiFailure, errorText, fieldErrors, request } from '../lib/api';
import { BrandTile, Icon, Wordmark } from '../ui/Icon';
import { Button, Field, Input, Select } from '../ui/kit';

/** Pakistan and the Gulf first (as 1.1's sign-up), then the rest by name. */
export const COUNTRIES: [string, string][] = [
  ['PK', 'Pakistan'], ['SA', 'Saudi Arabia'], ['AE', 'United Arab Emirates'], ['QA', 'Qatar'], ['KW', 'Kuwait'], ['BH', 'Bahrain'], ['OM', 'Oman'],
  ['AF', 'Afghanistan'], ['AU', 'Australia'], ['BD', 'Bangladesh'], ['CA', 'Canada'], ['EG', 'Egypt'], ['FR', 'France'], ['DE', 'Germany'], ['IN', 'India'],
  ['IE', 'Ireland'], ['IT', 'Italy'], ['JO', 'Jordan'], ['KE', 'Kenya'], ['MY', 'Malaysia'], ['NP', 'Nepal'], ['NL', 'Netherlands'], ['NZ', 'New Zealand'],
  ['NG', 'Nigeria'], ['SG', 'Singapore'], ['ZA', 'South Africa'], ['ES', 'Spain'], ['LK', 'Sri Lanka'], ['TR', 'Turkey'], ['GB', 'United Kingdom'], ['US', 'United States'],
];
const ZONE: Record<string, string> = { 'Asia/Karachi': 'PK', 'Asia/Riyadh': 'SA', 'Asia/Dubai': 'AE', 'Asia/Qatar': 'QA', 'Asia/Kuwait': 'KW', 'Asia/Bahrain': 'BH', 'Asia/Muscat': 'OM', 'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN', 'Asia/Dhaka': 'BD', 'Europe/London': 'GB', 'Asia/Colombo': 'LK', 'Asia/Kathmandu': 'NP', 'Asia/Kabul': 'AF' };
function guessCountry(): string {
  try {
    return ZONE[Intl.DateTimeFormat().resolvedOptions().timeZone] ?? 'PK';
  } catch {
    return 'PK';
  }
}

/** The dark brand panel with the language switch, shared by the public pages of my.buildiary.app. */
export function PublicFrame({ kicker, headline, lead, children }: { kicker: string; headline: string; lead?: string; children: ReactNode }) {
  const t = useT();
  const { lang, setLang } = useLang();
  return (
    <main className="signin">
      <section className="signin-hero">
        <div className="row" style={{ gap: 12 }}><BrandTile size={44} /><Wordmark width={136} /></div>
        <div className="stack hero-copy" style={{ marginTop: 40, gap: 18, maxWidth: 520 }}>
          <span className="kicker">{kicker}</span>
          <h1>{headline}</h1>
          {lead && <p className="hero-p">{lead}</p>}
        </div>
        <div className="row-wrap between" style={{ marginTop: 'auto', gap: 16 }}>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,.72)' }}>{t('si.endorse')}</span>
          <div className="hero-lang" role="group" aria-label={t('top.language')}>
            {LANGS.map((l) => (
              <button key={l} type="button" lang={l} aria-pressed={lang === l} onClick={() => setLang(l)}>{LANG_NAME[l]}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="signin-main" style={{ maxWidth: 760 }}>{children}</section>
    </main>
  );
}

const money = (p: { currency: string; amount: number }) => formatMoney(p.amount, p.currency);

function PlanChoice({ plan, picked, onPick }: { plan: PublicPlan; picked: boolean; onPick: () => void }) {
  const t = useT();
  const { lang } = useLang();
  return (
    <button type="button" className="choice" aria-pressed={picked} onClick={onPick}>
      <span className="n">{plan.mode === 'builder' ? t('st.builder') : plan.mode === 'owner' ? t('st.owner') : plan.name} · {plan.name}</span>
      <span className="p">{t('st.perMonth', { price: money(plan.month) })}{plan.month.currency !== 'USD' ? ` ($${plan.month.usd})` : ''}</span>
      <span className="s">{t('st.perYear', { price: money(plan.year) })}</span>
      <span className="s">{plan.summary[lang] || plan.summary.en}</span>
      {plan.projects && plan.users && <span className="s">{t('st.limits', { projects: plan.projects, users: plan.users })}</span>}
    </button>
  );
}

/** /start on my.buildiary.app: the self-serve sign-up (1.1's start page), then the email code. */
export function StartPage() {
  const t = useT();
  const host = useHost();
  const { lang } = useLang();
  const [sp] = useSearchParams();
  const [country, setCountry] = useState(() => (sp.get('country') ?? '').toUpperCase() || guessCountry());
  const [plans, setPlans] = useState<SignupPlans | null>(null);
  const [plan, setPlan] = useState(sp.get('plan') === 'builder' ? 'builder' : 'owner');
  const [startWith, setStartWith] = useState<'empty' | 'sample'>('empty');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState<{ ok: boolean; message: string | null; host: string } | null>(null);
  const [agree, setAgree] = useState(false);
  const [hp, setHp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fe, setFe] = useState<Record<string, string>>({});
  const [started, setStarted] = useState<StartResult | null>(null);

  useEffect(() => {
    let live = true;
    request('GET /api/start/plans', { query: { country } }).then((p) => live && setPlans(p), () => undefined);
    return () => { live = false; };
  }, [country]);

  // The address: suggested from the workspace name until the person types their own; checked as it is typed.
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!slugTouched && company.trim()) {
        request('GET /api/start/check', { query: { company } }).then((r) => { setSlug(r.slug); setSlugState(r); }, () => undefined);
      } else if (slug) {
        request('GET /api/start/check', { query: { slug } }).then(setSlugState, () => undefined);
      } else setSlugState(null);
    }, 350);
    return () => window.clearTimeout(timer.current);
  }, [company, slug, slugTouched]);

  const picked = useMemo(() => plans?.plans.find((p) => p.code === plan) ?? plans?.plans[0] ?? null, [plans, plan]);
  const submit = async () => {
    const f: Record<string, string> = {};
    if (!name.trim()) f.name = t('c.required');
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) f.email = t('c.required');
    if (phone.replace(/\D/g, '').length < 7) f.phone = t('c.required');
    if (!company.trim()) f.company = t('c.required');
    if (!slug || slugState?.ok === false) f.slug = slugState?.message ?? t('c.required');
    if (!agree) f.agree = t('c.required');
    setFe(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await request('POST /api/start', { body: { plan: picked?.code ?? plan, startWith, name: name.trim(), email: email.trim(), country, phone: phone.trim(), company: company.trim(), slug, lang, agree, website: hp || undefined } });
      setStarted(r);
    } catch (e) {
      setErr(errorText(e));
      setFe(fieldErrors(e));
    } finally {
      setBusy(false);
    }
  };

  if (started) return <VerifyStep started={started} />;
  const site = host.websiteUrl ?? 'https://buildiary.app';
  return (
    <PublicFrame kicker="Buildiary" headline={t('st.title')} lead={t('st.lead', { days: plans?.trialDays ?? 14 })}>
      <div className="stack" style={{ gap: 6 }}>
        <h2>{t('st.title')}</h2>
        <p className="muted" style={{ fontSize: 15 }}>{t('st.lead', { days: plans?.trialDays ?? 14 })}</p>
      </div>
      {plans && !plans.open && <div className="alert warning" role="alert"><Icon name="warn" size={16} />{t('st.closed')}</div>}
      <form className="form-grid" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        <fieldset className="span-2 stack" style={{ border: 0, padding: 0, margin: 0, gap: 8 }}>
          <legend className="section-label" style={{ marginBottom: 8 }}>{t('st.what')}</legend>
          <div className="choice-grid">
            {(plans?.plans ?? []).map((p) => <PlanChoice key={p.code} plan={p} picked={picked?.code === p.code} onPick={() => setPlan(p.code)} />)}
          </div>
        </fieldset>
        <fieldset className="span-2 stack" style={{ border: 0, padding: 0, margin: 0, gap: 8 }}>
          <legend className="section-label" style={{ marginBottom: 8 }}>{t('st.data')}</legend>
          <div className="choice-grid">
            <button type="button" className="choice" aria-pressed={startWith === 'empty'} onClick={() => setStartWith('empty')}><span className="n">{t('st.empty')}</span></button>
            <button type="button" className="choice" aria-pressed={startWith === 'sample'} onClick={() => setStartWith('sample')}><span className="n">{t('st.sample')}</span></button>
          </div>
        </fieldset>
        <Field label={t('st.name')} error={fe.name}><Input autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} invalid={!!fe.name} /></Field>
        <Field label={t('st.email')} error={fe.email}><Input type="email" autoComplete="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} invalid={!!fe.email} /></Field>
        <Field label={t('st.country')} error={fe.country}>
          <Select value={country} onChange={(e) => setCountry(e.target.value)}>
            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </Select>
        </Field>
        <Field label={t('st.phone')} error={fe.phone}><Input type="tel" autoComplete="tel" dir="ltr" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} invalid={!!fe.phone} placeholder={country === 'PK' ? '0300 1234567' : '+'} /></Field>
        <Field label={t('st.company')} hint={t('st.companyHint')} error={fe.company} className="span-2"><Input value={company} onChange={(e) => setCompany(e.target.value)} invalid={!!fe.company} /></Field>
        <Field label={t('st.address')} error={fe.slug ?? (slugState && !slugState.ok ? slugState.message : null)} hint={slugState?.ok ? `✓ ${t('st.free')}: ${slugState.host}` : undefined} className="span-2">
          <div className="slug-field">
            <span className="sfx" style={{ borderInlineStart: 0, borderInlineEnd: '1px solid var(--line)' }}>https://</span>
            <input aria-label={t('st.address')} value={slug} autoCapitalize="none" spellCheck={false} onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }} />
            <span className="sfx">.{host.baseDomain ?? 'buildiary.app'}</span>
          </div>
        </Field>
        <label className="span-2 row" style={{ gap: 10, alignItems: 'flex-start', minHeight: 44 }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ width: 20, height: 20, marginTop: 2 }} />
          <span>{t('st.agree')} (<a href={`${site}/terms/`} target="_blank" rel="noreferrer">{t('st.terms')}</a>)</span>
        </label>
        {fe.agree && <span className="err danger-text small span-2" role="alert">{fe.agree}</span>}
        <label className="hp" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} /></label>
        {err && <div className="alert danger span-2" role="alert"><Icon name="warn" size={16} />{err}</div>}
        <Button type="submit" variant="primary" className="span-2" loading={busy} disabled={plans ? !plans.open : false} style={{ minHeight: 48, fontSize: 15 }}>{t('st.go')}</Button>
      </form>
      <p className="muted small">{t('st.have')} <Link to="/find">{t('si.find')}</Link></p>
    </PublicFrame>
  );
}

function VerifyStep({ started }: { started: StartResult }) {
  const t = useT();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const verify = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await request('POST /api/start/verify', { body: { token: started.token, code } });
      window.location.assign(r.setPasswordUrl ?? r.url);
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };
  const resend = async () => {
    try {
      await request('POST /api/start/resend', { body: { token: started.token } });
      setNote(t('st.resent'));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  return (
    <PublicFrame kicker={started.host} headline={t('st.checkTitle')}>
      <div className="stack" style={{ gap: 6 }}>
        <h2>{t('st.checkTitle')}</h2>
        <p className="muted" style={{ fontSize: 15 }}>{t('st.checkLead', { email: started.email })}</p>
      </div>
      {started.mailOff && <div className="alert info"><Icon name="info" size={16} />{t('st.mailOff')}</div>}
      <form className="form" onSubmit={(e) => { e.preventDefault(); void verify(); }} noValidate>
        <Field label={t('st.code')} error={err}>
          <Input className="code-input" inputMode="numeric" autoComplete="one-time-code" dir="ltr" value={code} maxLength={6} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} invalid={!!err} />
        </Field>
        {note && <div className="alert success" role="status"><Icon name="tick" size={16} />{note}</div>}
        <div className="row-wrap" style={{ gap: 10 }}>
          <Button type="submit" variant="primary" loading={busy} disabled={code.length !== 6} style={{ minHeight: 46 }}>{t('st.confirm')}</Button>
          <Button onClick={() => void resend()}>{t('st.resend')}</Button>
        </div>
      </form>
    </PublicFrame>
  );
}

/** /start/verify?t=<link> from the email: confirms with one tap and opens the workspace. */
export function VerifyLinkPage() {
  const t = useT();
  const [sp] = useSearchParams();
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ url: string; host: string } | null>(null);
  useEffect(() => {
    const link = sp.get('t') ?? '';
    request('POST /api/start/verify', { body: { link } }).then(
      (r) => {
        setDone(r);
        window.location.assign(r.setPasswordUrl ?? r.url);
      },
      (e: unknown) => setErr(e instanceof ApiFailure && e.status === 410 ? e.body.message : errorText(e)),
    );
  }, [sp]);
  return (
    <PublicFrame kicker="Buildiary" headline={done ? t('st.ready') : t('st.checkTitle')}>
      {err ? (
        <>
          <div className="alert danger" role="alert"><Icon name="warn" size={16} />{err}</div>
          <Link className="btn primary" to="/start" style={{ alignSelf: 'flex-start' }}>{t('st.title')}</Link>
        </>
      ) : done ? (
        <a className="btn primary" href={done.url} style={{ alignSelf: 'flex-start' }}>{t('st.openIt', { host: done.host })}</a>
      ) : (
        <p className="muted">{t('st.opening')}</p>
      )}
    </PublicFrame>
  );
}
